'use client';

import { useSearchParams } from 'next/navigation';
import { signIn } from 'next-auth/react';
import Link from '@mui/material/Link';
import type { MouseEventHandler } from 'react';
import type { SxProps, Theme } from '@mui/material';

/**
 * if `login`: Always prompt the user for authentication, regardless of whether they have an Okta session.
 * if `none`: Then the default Okta behavior occurs. If an Okta session already exists, the user is silently authenticated. Otherwise, the user is prompted to authenticate.
 */
export type OktaPromptParameterValues = 'login' | 'none';

/**
 * Presents inline text which acts as a button to initiate an end-user sign-in process with Okta.
 * Currently, it's not configurable between IdPs or sign-in flows/requirements, so I'm labeling it
 * as an Okta sign-in button.
 *
 * Reads the current URL search params to see if `callbackUrl` is specified, and sets that as the callback URL for auth.
 *
 */
function OktaSignInButton({ prompt, sx }: { prompt?: OktaPromptParameterValues; sx?: SxProps<Theme> }) {
    const searchParams = useSearchParams();
    const callbackUrl = searchParams.get('callbackUrl') ?? '/dashboard';

    const handleSignIn: MouseEventHandler<HTMLButtonElement> = e => {
        e?.preventDefault();
        void signIn('okta', { callbackUrl: callbackUrl }, !prompt ? {} : { prompt });
    };

    const defaultSx: SxProps<Theme> = { marginTop: '-0.15rem', textDecoration: 'underline' };
    const mergedSx = typeof sx === 'object' ? { ...defaultSx, ...sx } : defaultSx;

    /* TODO: Style this better */
    return (
        <Link component="button" onClick={handleSignIn} sx={mergedSx}>
            sign in
        </Link>
    );
}

export default OktaSignInButton;
